import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, FileText, AlertTriangle, Users, MessageSquare, 
  Trash2, Check, X, Eye, Send, RefreshCw, BarChart3, Webhook, Twitter
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  reviewing: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  published: 'bg-purple-100 text-purple-800'
};

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tips, setTips] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipFilter, setTipFilter] = useState('pending');
  const [scraping, setScraping] = useState(false);
  
  // Webhook state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(true);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);

  useEffect(() => {
    if (!authLoading && user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
      fetchWebhookConfig();
    }
  }, [user, tipFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, tipsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/tips`, { params: { status: tipFilter || undefined } }),
        axios.get(`${API}/admin/articles`, { params: { limit: 50 } })
      ]);
      setStats(statsRes.data);
      setTips(tipsRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/webhook`);
      setWebhookUrl(res.data.webhook_url || '');
      setWebhookEnabled(res.data.enabled);
    } catch (error) {
      console.error('Failed to fetch webhook config:', error);
    }
  };

  const handleSaveWebhook = async () => {
    setWebhookLoading(true);
    try {
      await axios.post(`${API}/admin/webhook`, {
        webhook_url: webhookUrl,
        enabled: webhookEnabled
      });
      toast.success('Webhook configuration saved');
    } catch (error) {
      toast.error('Failed to save webhook configuration');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleTestWebhook = async () => {
    if (!webhookUrl) {
      toast.error('Please enter a webhook URL first');
      return;
    }
    setWebhookTesting(true);
    try {
      await axios.post(`${API}/admin/webhook/test`);
      toast.success('Test webhook sent! Check your Zapier/Make dashboard.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test webhook');
    } finally {
      setWebhookTesting(false);
    }
  };

  const handleUpdateTipStatus = async (tipId, newStatus) => {
    try {
      await axios.put(`${API}/admin/tips/${tipId}`, { status: newStatus });
      toast.success(`Tip status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update tip status');
    }
  };

  const handlePublishTip = async (tipId) => {
    try {
      await axios.post(`${API}/admin/tips/${tipId}/publish`);
      toast.success('Tip published as article');
      fetchData();
    } catch (error) {
      toast.error('Failed to publish tip');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/articles/${articleId}`);
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleScrape = async () => {
    setScraping(true);
    try {
      await axios.post(`${API}/scrape`);
      toast.success('Comprehensive scrape initiated. This may take a few minutes.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start scrape');
    } finally {
      setScraping(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md mx-auto p-8">
          <Shield className="h-16 w-16 mx-auto mb-6 text-slate-300" />
          <h1 className="font-serif text-2xl font-medium mb-4">Admin Access Required</h1>
          <p className="text-slate-500 mb-6">Please log in with an admin account to access this panel.</p>
          <Link to="/login">
            <Button className="btn-primary">Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md mx-auto p-8">
          <Shield className="h-16 w-16 mx-auto mb-6 text-red-300" />
          <h1 className="font-serif text-2xl font-medium mb-4">Access Denied</h1>
          <p className="text-slate-500 mb-6">Your account does not have admin privileges.</p>
          <Link to="/">
            <Button variant="outline">Return to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      {/* Header */}
      <div className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-serif font-medium">Admin Panel</h1>
                <p className="text-slate-400 text-sm">Manage articles, tips, and content</p>
              </div>
            </div>
            <Button 
              onClick={handleScrape} 
              disabled={scraping}
              className="bg-white text-black hover:bg-slate-200"
              data-testid="admin-scrape-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${scraping ? 'animate-spin' : ''}`} />
              {scraping ? 'Scraping...' : 'Run Full Scrape'}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3">
                <FileText className="h-8 w-8 text-slate-400" />
                <div>
                  <p className="text-2xl font-mono font-bold">{stats.articles?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Articles</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-mono font-bold">{stats.tips?.pending || 0}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Pending Tips</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3">
                <Users className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-mono font-bold">{stats.users || 0}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3">
                <MessageSquare className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-2xl font-mono font-bold">{stats.comments || 0}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Comments</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="tips" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="tips" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Tips ({tips.length})
            </TabsTrigger>
            <TabsTrigger value="articles" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Articles ({articles.length})
            </TabsTrigger>
            <TabsTrigger value="webhook" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Webhook className="h-4 w-4 mr-2" />
              Social Media
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Statistics
            </TabsTrigger>
          </TabsList>

          {/* Tips Tab */}
          <TabsContent value="tips">
            <div className="bg-white border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-medium">Whistleblower Tips</h2>
                <Select value={tipFilter} onValueChange={setTipFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tips</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : tips.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No tips found with this status.
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {tips.map((tip) => (
                    <div key={tip.id} className="p-4" data-testid={`tip-${tip.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="font-mono text-sm bg-slate-100 px-2 py-1">
                              {tip.reference_code}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${statusColors[tip.status]}`}>
                              {tip.status.toUpperCase()}
                            </span>
                            <span className="text-xs text-slate-500 uppercase">
                              {tip.party}
                            </span>
                          </div>
                          <h3 className="font-medium mb-1">{tip.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-2">{tip.content}</p>
                          <div className="flex items-center space-x-4 text-xs text-slate-500">
                            {tip.politician_name && <span>Re: {tip.politician_name}</span>}
                            {tip.region && <span>{tip.region}</span>}
                            {tip.contact_email && <span>📧 Has contact</span>}
                            <span>{format(new Date(tip.created_at), 'dd MMM yyyy HH:mm')}</span>
                          </div>
                          {tip.evidence_urls?.length > 0 && (
                            <div className="mt-2">
                              <span className="text-xs text-slate-500">Evidence links: {tip.evidence_urls.length}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {tip.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleUpdateTipStatus(tip.id, 'reviewing')}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                            </>
                          )}
                          {tip.status === 'reviewing' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600 border-green-600"
                                onClick={() => handleUpdateTipStatus(tip.id, 'approved')}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 border-red-600"
                                onClick={() => handleUpdateTipStatus(tip.id, 'rejected')}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {tip.status === 'approved' && (
                            <Button 
                              size="sm" 
                              onClick={() => handlePublishTip(tip.id)}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Publish
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Articles Tab */}
          <TabsContent value="articles">
            <div className="bg-white border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-medium">All Articles</h2>
              </div>

              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {articles.map((article) => (
                    <div key={article.id} className="p-4 flex items-start justify-between" data-testid={`article-admin-${article.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`text-xs px-2 py-1 border ${
                            article.party === 'labour' ? 'text-red-600 border-red-600' :
                            article.party === 'conservative' ? 'text-blue-600 border-blue-600' :
                            'text-green-600 border-green-600'
                          }`}>
                            {article.party.toUpperCase()}
                          </span>
                          <span className="text-xs text-slate-400">{article.source}</span>
                          {article.region && (
                            <span className="text-xs text-slate-500">{article.region}</span>
                          )}
                        </div>
                        <h3 className="font-medium text-sm line-clamp-1">{article.title}</h3>
                        <p className="text-xs text-slate-500 mt-1">
                          {format(new Date(article.created_at), 'dd MMM yyyy')} • {article.comment_count} comments
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteArticle(article.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Webhook/Social Media Tab */}
          <TabsContent value="webhook">
            <div className="bg-white border border-slate-200 shadow-sm">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center space-x-3 mb-2">
                  <Twitter className="h-6 w-6 text-slate-600" />
                  <h2 className="font-medium text-lg">Auto-Post to Social Media</h2>
                </div>
                <p className="text-sm text-slate-500">
                  Connect to Zapier or Make.com to automatically post new articles to X, Facebook, and other platforms.
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Setup Instructions */}
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-medium text-sm mb-3">Quick Setup Guide:</h3>
                  <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                    <li>Create a free account at <a href="https://zapier.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Zapier.com</a> or <a href="https://make.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Make.com</a></li>
                    <li>Create a new Zap/Scenario with trigger: <strong>"Webhooks by Zapier"</strong> → <strong>"Catch Hook"</strong></li>
                    <li>Copy the webhook URL and paste it below</li>
                    <li>Add an action to post to X (Twitter), Facebook, etc.</li>
                    <li>Use the <strong>tweet_with_url</strong> field for pre-formatted posts</li>
                  </ol>
                </div>

                {/* Webhook URL Input */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Webhook URL</label>
                  <div className="flex space-x-3">
                    <Input
                      type="url"
                      placeholder="https://hooks.zapier.com/hooks/catch/..."
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      className="flex-1"
                      data-testid="webhook-url-input"
                    />
                    <Button 
                      onClick={handleSaveWebhook}
                      disabled={webhookLoading}
                      className="bg-slate-900 text-white"
                    >
                      {webhookLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Paste your Zapier/Make webhook URL here. New articles will be sent automatically when scraped.
                  </p>
                </div>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between py-3 border-t border-slate-200">
                  <div>
                    <p className="font-medium text-sm">Auto-posting enabled</p>
                    <p className="text-xs text-slate-500">When enabled, new articles will trigger the webhook</p>
                  </div>
                  <Button
                    variant={webhookEnabled ? "default" : "outline"}
                    size="sm"
                    onClick={() => setWebhookEnabled(!webhookEnabled)}
                    className={webhookEnabled ? 'bg-green-600' : ''}
                  >
                    {webhookEnabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>

                {/* Test Button */}
                <div className="flex items-center justify-between py-3 border-t border-slate-200">
                  <div>
                    <p className="font-medium text-sm">Test Webhook</p>
                    <p className="text-xs text-slate-500">Send a test article to verify your connection</p>
                  </div>
                  <Button
                    onClick={handleTestWebhook}
                    disabled={webhookTesting || !webhookUrl}
                    variant="outline"
                    data-testid="test-webhook-btn"
                  >
                    <Send className={`h-4 w-4 mr-2 ${webhookTesting ? 'animate-pulse' : ''}`} />
                    {webhookTesting ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>

                {/* Webhook Payload Info */}
                <div className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono">
                  <p className="text-slate-400 mb-2">// Webhook payload fields you can use in Zapier/Make:</p>
                  <pre className="whitespace-pre-wrap">{`{
  "event": "new_article",
  "title": "Article headline...",
  "url": "https://yoursite.com/article/...",
  "summary": "Article summary text...",
  "party": "labour",
  "party_label": "Labour",
  "source_name": "BBC News",
  "tweet_text": "Headline via @TheWatchtowerPo",
  "tweet_with_url": "Headline [url] via @TheWatchtowerPo"
}`}</pre>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 shadow-sm p-6">
                  <h3 className="font-medium mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Articles by Party
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-red-600">Labour</span>
                      <span className="font-mono font-bold">{stats.articles?.by_party?.labour || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-600">Conservative</span>
                      <span className="font-mono font-bold">{stats.articles?.by_party?.conservative || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-green-600">Green</span>
                      <span className="font-mono font-bold">{stats.articles?.by_party?.green || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 shadow-sm p-6">
                  <h3 className="font-medium mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    Tips Summary
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span>Total Tips</span>
                      <span className="font-mono font-bold">{stats.tips?.total || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-yellow-600">Pending</span>
                      <span className="font-mono font-bold">{stats.tips?.pending || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-600">Reviewing</span>
                      <span className="font-mono font-bold">{stats.tips?.reviewing || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
